import { useCallback, useEffect, useRef } from "react";
import { bool } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Tables, hashCode } from "@repo/utils";
import { useStores } from "@hooks";
import {
  useEstimateStructure,
  usePresetFieldNames,
  useEstimateEditorStore,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  useTableExpandHandler,
  Section,
  NewSectionButton,
  TableTitle,
} from "@tools";
import { SortableList } from "@components";
import { Collapse } from "@material-ui/core";
import { BreakdownTableActions } from "./components/BreakdownTableActions";
import { TableBreakdownContainer } from "./TableBreakdownContainer";
import useStyle from "@tools/EstimateEditor/styles/CommonTableStyles.style";
import classnames from "classnames";

export const TableBreakdown = observer(({
  forceCollapseReveal,
  isLastTable,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { stateStore } = useStores();
  const { visibilityMode, isAiGenerating } = useEstimateEditorStore();
  const {
    isProposal,
    onSectionReorder,
    blockExpansions,
    showPdfCover,
    allowEdition,
    previewMode,
    isPdfPrinterView,
  } = useEstimateEditorSettings();
  const { getFieldName } = usePresetFieldNames(isProposal);
  const classes = useStyle();

  const { displaySections, settings, visibility, hasSearchPhrase } = structure;
  const { showBreakdown } = settings;
  const { hideBreakdown } = visibility;
  
  const className = useVisibilityModeCellClassName(hideBreakdown);

  const handleSectionReorder = (list) => {
    structure.reorderSections(list);
  }
  
  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: !blockExpansions,
    isOpened: showBreakdown,
    onExpand: settings.setShowBreakdown
  });
  
  const mutationRef = useRef(null);
  const scrollToBottom = useCallback(() => {
    stateStore.scrollRef?.scrollTo({ top: stateStore.scrollRef.scrollHeight, behavior: "smooth" });
  });
  
  useEffect(() => {
    if (mutationRef.current && isAiGenerating) {
      const observer = new MutationObserver(scrollToBottom);
      observer.observe(mutationRef.current, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
      });
      return () => {
        observer.disconnect();
        stateStore.scrollRef?.scrollTo({ top: 0, behavior: "smooth" });
      };
    }
  }, [mutationRef, isAiGenerating]);

  return (
    <TableBreakdownContainer isLastTable={isLastTable}>
      <TableTitle
        disabled={visibilityMode && hideBreakdown}
        expandable={!blockExpansions}
        presetClass="breakdown"
        buttonPresetClass="preset-tableBreakdownExpandButton"
        id={hashCode(Tables.BREAKDOWN)}
        title={getFieldName("pt-breakdown")}
        onContentAction={handleOpen}
        showContent={showBreakdown || forceCollapseReveal}
        className={classnames("preset-page preset-noedit", isPdfPrinterView && "printer-mode")}
        action={!isPdfPrinterView && <BreakdownTableActions />}
      />
      <Collapse
        ref={mutationRef}
        in={showBreakdown || forceCollapseReveal}
        timeout="auto"
        className={classnames(
          showPdfCover && "pdf-mode",
          previewMode && "preview-mode",
          isPdfPrinterView && "printer-mode",
          classes.collapse,
          className
        )}
        mountOnEnter
        unmountOnExit
        data-id={hashCode("cont")}
        data-t={Tables.BREAKDOWN}
        data-p={hashCode(Tables.BREAKDOWN)}
        { ...collapseProps }
      >
        <SortableList
          list={displaySections}
          path={hashCode(Tables.BREAKDOWN)}
          group="sections"
          onListReorderStart={onSectionReorder}
          onListReorder={handleSectionReorder}
          disabled={!allowEdition || structure.hasSearchPhrase}
          // className={classes.tableContainerWrapper}
          displayAsColumnFlexbox
        >
          {
            displaySections.map((section, index) => (
              <Section
                key={`section${section.id}`}
                section={section}
                index={index}
              />
            ))
          }
        </SortableList>
        {
          !displaySections.length && hasSearchPhrase &&
          <h3 className="transparent-3 pb-6 px-10">
            {t("views.editor.breakdown_table.not_found")}
          </h3>
        }
        {allowEdition && <NewSectionButton />}
      </Collapse>
    </TableBreakdownContainer>
  );
});

TableBreakdown.propTypes = {
  forceCollapseReveal: bool,
  isLastTable: bool,
}